import { useState, useEffect, useCallback, useRef } from 'react';
import { DeviceCheckResult } from 'components/MediaDevicesCheck';
import { isCapacitor } from 'utils/platform';

/** Безопасно останавливает все треки стрима */
const stopStream = (stream: MediaStream | null) => {
  if (!stream) return;
  stream.getTracks().forEach((track) => {
    console.log('[useMediaDevices] Останавливаем трек:', track.kind, track.label, 'readyState:', track.readyState);
    track.stop();
  });
};

interface UseMediaDevicesOptions {
  /**
   * Запускать ли проверку устройств автоматически при монтировании.
   * По умолчанию true. На CheckDevices ставим false, чтобы не запрашивать
   * доступ к камере/микрофону сразу при заходе — только по действию пользователя.
   */
  autoStart?: boolean;
}

export const useMediaDevices = ({ autoStart = true }: UseMediaDevicesOptions = {}) => {
  const [devicesResult, setDevicesResult] = useState<DeviceCheckResult | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref для отмены текущей проверки при размонтировании / повторном вызове
  const abortRef = useRef(false);
  // Коллекция всех стримов, полученных в текущей проверке — для гарантированной очистки
  const activeStreamsRef = useRef<MediaStream[]>([]);
  // Запускалась ли проверка устройств хотя бы раз. Нужно, чтобы при autoStart=false
  // случайный devicechange не вызвал getUserMedia до первого действия пользователя.
  const hasCheckedRef = useRef(false);

  /** Останавливает все стримы из activeStreamsRef */
  const stopAllActiveStreams = useCallback(() => {
    activeStreamsRef.current.forEach(stopStream);
    activeStreamsRef.current = [];
  }, []);

  /** Регистрирует стрим для последующей очистки */
  const trackStream = useCallback((stream: MediaStream) => {
    activeStreamsRef.current.push(stream);
    return stream;
  }, []);

  const checkDevices = useCallback(async () => {
    // Отменяем предыдущую проверку и чистим её стримы
    abortRef.current = true;
    stopAllActiveStreams();
    abortRef.current = false;
    hasCheckedRef.current = true;

    setIsChecking(true);
    setError(null);

    // Локальная переменная, привязанная к текущему вызову
    const cancelled = () => abortRef.current;

    try {
      // Проверяем поддержку MediaDevices API
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        throw new Error('MediaDevices API не поддерживается в вашем браузере');
      }

      const result: DeviceCheckResult = {
        microphone: {
          available: false,
          devices: [],
          hasPermission: false
        },
        camera: {
          available: false,
          devices: [],
          hasPermission: false
        }
      };

      // Получаем список всех устройств
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (cancelled()) return;

      // Фильтруем микрофоны и камеры
      const microphones = devices.filter((device) => device.kind === 'audioinput');
      const cameras = devices.filter((device) => device.kind === 'videoinput');

      result.microphone.available = microphones.length > 0;
      result.microphone.devices = microphones;
      result.camera.available = cameras.length > 0;
      result.camera.devices = cameras;

      // Проверяем разрешения для микрофона (устойчивая инициализация с ретраями)
      try {
        // Набор стратегий для получения аудио-потока
        const audioConstraintsVariants: MediaStreamConstraints[] = [
          { audio: true },
          {
            audio: {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          }
        ];

        // Добавляем попытки со всеми доступными микрофонами (часто решает NotReadableError на macOS)
        if (microphones.length > 0) {
          microphones.forEach((device) => {
            audioConstraintsVariants.push({
              audio: {
                deviceId: device.deviceId ? { exact: device.deviceId } : undefined,
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false
              }
            } as MediaStreamConstraints);
          });
        }

        let micStream: MediaStream | null = null;
        let lastError: unknown = null;
        for (const constraints of audioConstraintsVariants) {
          if (cancelled()) return;
          try {
            micStream = trackStream(await navigator.mediaDevices.getUserMedia(constraints));
            break;
          } catch (e) {
            lastError = e;
            continue;
          }
        }

        if (cancelled()) return;

        if (!micStream) {
          // Улучшаем диагностику для NotReadableError
          const message =
            (lastError as DOMException)?.name === 'NotReadableError'
              ? 'Микрофон занят другой программой. Закройте приложения, использующие микрофон (Zoom/Meet/Skype/OBS) и попробуйте снова.'
              : lastError instanceof Error
              ? lastError.message
              : 'Не удалось получить доступ к микрофону';
          throw new Error(message);
        }

        result.microphone.hasPermission = true;

        // Останавливаем поток
        stopStream(micStream);
      } catch (err) {
        result.microphone.hasPermission = false;
      }

      if (cancelled()) return;

      // Проверяем разрешения для камеры с улучшенными параметрами
      try {
        // Пробуем получить максимально возможное качество
        const videoConstraints = [
          // Попытка 1: Максимальное качество
          {
            video: {
              width: { ideal: 1920, min: 1280 },
              height: { ideal: 1080, min: 720 },
              frameRate: { ideal: 30, min: 25 }
            }
          },
          // Попытка 2: HD качество
          {
            video: {
              width: { ideal: 1280, min: 1024 },
              height: { ideal: 720, min: 576 },
              frameRate: { ideal: 30, min: 20 }
            }
          },
          // Попытка 3: Базовое качество
          {
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30 }
            }
          },
          // Попытка 4: Минимальные требования
          {
            video: true
          }
        ];

        let videoStream: MediaStream | null = null;
        let bestSettings: MediaTrackSettings | null = null;

        // Пробуем каждую конфигурацию до получения успешного результата
        for (const constraints of videoConstraints) {
          if (cancelled()) return;
          try {
            // Останавливаем предыдущий стрим перед получением нового
            if (videoStream) {
              stopStream(videoStream);
            }
            videoStream = trackStream(await navigator.mediaDevices.getUserMedia(constraints));
            const videoTrack = videoStream.getVideoTracks()[0];
            if (videoTrack) {
              const settings = videoTrack.getSettings();
              // Если это первая успешная попытка или качество лучше предыдущего
              if (
                !bestSettings ||
                (settings.width &&
                  settings.height &&
                  settings.width * settings.height >
                    (bestSettings.width || 0) * (bestSettings.height || 0))
              ) {
                bestSettings = settings;
                // Если получили хорошее качество, останавливаем поиск
                if (
                  settings.width &&
                  settings.height &&
                  settings.width * settings.height >= 1280 * 720
                ) {
                  break;
                }
              }
            }
          } catch (err) {
            // Продолжаем с следующей конфигурацией
            continue;
          }
        }

        if (cancelled()) return;

        if (videoStream && bestSettings) {
          result.camera.hasPermission = true;
          const width = bestSettings.width || 0;
          const height = bestSettings.height || 0;
          const frameRate = bestSettings.frameRate || 0;

          result.camera.resolution = { width, height };
          result.camera.fps = frameRate;

          // Останавливаем поток
          stopStream(videoStream);
        } else {
          throw new Error('Не удалось получить доступ к камере');
        }
      } catch (err) {
        result.camera.hasPermission = false;
      }

      if (cancelled()) return null;

      setDevicesResult(result);
      return result;
    } catch (err) {
      if (!cancelled()) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка при проверке устройств');
      }
      return null;
    } finally {
      if (!cancelled()) {
        setIsChecking(false);
      }
      // Гарантированно останавливаем все стримы после завершения проверки
      stopAllActiveStreams();
    }
  }, [stopAllActiveStreams, trackStream]);

  useEffect(() => {
    if (autoStart) {
      checkDevices();
    }

    // При размонтировании: отменяем проверку и останавливаем все стримы
    return () => {
      abortRef.current = true;
      stopAllActiveStreams();
    };
  }, [autoStart, checkDevices, stopAllActiveStreams]);

  // Автообновление при изменении состава/статуса медиа устройств.
  // На iOS/Capacitor devicechange вызывает каскад checkDevices и stopStream — это мешает
  // видео на VideoInterviewRecordPage, CheckDevices и Settings (серый экран).
  useEffect(() => {
    if (isCapacitor() || !navigator.mediaDevices?.addEventListener) {
      return;
    }

    const handleDeviceChange = () => {
      // Не дёргаем getUserMedia до первой ручной проверки — иначе на CheckDevices
      // (autoStart=false) случайный devicechange вызвал бы преждевременный запрос доступа.
      if (!hasCheckedRef.current) return;
      checkDevices();
    };

    try {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    } catch (_) {
      // ignore
    }

    return () => {
      try {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      } catch (_) {
        // ignore
      }
    };
  }, [checkDevices]);

  // Одноразовое обновление списка устройств сразу после grant, чтобы получить реальные label/deviceId
  const didRefreshAfterGrantRef = useRef(false);
  useEffect(() => {
    const shouldRefresh =
      !!devicesResult?.microphone.hasPermission || !!devicesResult?.camera.hasPermission;
    if (!shouldRefresh || didRefreshAfterGrantRef.current) {
      return;
    }

    let isCancelled = false;
    const refresh = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter((d) => d.kind === 'audioinput');
        const cameras = devices.filter((d) => d.kind === 'videoinput');

        if (isCancelled) {
          return;
        }

        setDevicesResult((prev) => {
          if (!prev) return prev;
          return {
            microphone: {
              ...prev.microphone,
              available: microphones.length > 0,
              devices: microphones as any
            },
            camera: {
              ...prev.camera,
              available: cameras.length > 0,
              devices: cameras as any
            }
          };
        });
        didRefreshAfterGrantRef.current = true;
      } catch (_) {
        // ignore
      }
    };

    refresh();
    return () => {
      isCancelled = true;
    };
  }, [devicesResult?.microphone.hasPermission, devicesResult?.camera.hasPermission]);

  const isDevicesReady =
    devicesResult && devicesResult.microphone.hasPermission && devicesResult.camera.hasPermission;

  return {
    devicesResult,
    isChecking,
    error,
    checkDevices,
    isDevicesReady
  };
};
